import { metadataTypeIgnoredFilters } from '@/components/Dashboard/Dashboard.utils';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { fetchMetadataColumns, successToast } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty } from '@/littledash';
import { ID } from '@/model/Common.model.ts';
import { web as webRoute } from '@/support/route';
import { sortAscByProperty } from '@/utils/array.utils';
import { selectOperations } from '@/utils/FiltersOperations.utils';
import { modalAction } from '@/utils/modal';
import { filterOptionsFromApi } from '../Colony.utils';
import CageActionsCell from '@/components/Colony/Cages/CageActions.tsx';
import { Cage as BaseCage } from '@/model/Cage.model';
import { Dispatch } from 'react';
import type { NavigateFunction } from 'react-router-dom';

// Extended Cage type for Colony components with additional API fields
export interface Cage extends BaseCage {
  study_link?: {
    data?: {
      id: number;
      name: string;
      access?: boolean;
      deleted_at?: string;
    };
  };
}

export interface CageFiltersEntity {
  fields: Array<{
    name: string;
    value: string;
    options: Array<Record<string, number>>;
  }>;
  metadata: Array<CageMetadataFilter>;
}

export interface CageMetadataFilter {
  id: ID;
  name: string;
  slug: string;
  field_type: string;
}

export const colonyCagesBulkActions = (
  {
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: {
    useTableProps: {
      selectedFlatRows: Array<{ original: Cage }>;
      apiTable: { refetchTableData: () => Promise<void> };
    };
  },
  handleDelete: (selectedCages: Array<Cage>, callback: (message: string) => void) => void,
  dispatch: Dispatch<{ type: string; data?: unknown }>,
  navigate: NavigateFunction
) => {
  const { openModal, closeModal } = modalAction(dispatch);
  const selectedTableRows = selectedFlatRows.map(({ original }) => original).sort(sortAscByProperty('id'));
  const callback = async (text: string) => {
    closeModal();
    successToast(text);
    await refetchTableData();
  };
  return [
    {
      name: 'Manage cages',
      key: 'manage_cages',
      ...validateManageCagesSelection(selectedTableRows),
      action: () => {
        const cageApiIds = selectedTableRows.map((cage) => cage.api_id);
        const studyId = selectedTableRows[0]?.study_id ?? null;

        // Pass data via route state
        navigate(webRoute('colony.manage_cages'), {
          state: { cageApiIds, studyId },
        });
      },
    },
    {
      name: 'Edit cage details',
      key: 'edit_details',
      ...validateManageCagesSelection(selectedTableRows),
      action: () => {
        sessionStorage.setItem('selectedColonyCages', JSON.stringify(selectedTableRows));
        navigate(webRoute('colony.edit.cages'));
      },
    },
    {
      name: 'Delete',
      ...validateManageCagesSelection(selectedTableRows),
      action: () =>
        openModal('CONFIRM_DELETE_COLLECTIONS', {
          subject: selectedTableRows,
          onClick: () => handleDelete(selectedTableRows, callback),
        }),
      className: 'red',
    },
  ];
};

export const validateManageCagesSelection = (selectedCages: Array<Cage> = [], limit: number = 300) => {
  const defaultResult = {
    disabled: false,
    tooltip: '',
  };

  if (_isEmpty(selectedCages)) {
    return defaultResult;
  }

  const allowedAccessPermissions = ['write'];

  if (selectedCages.length > limit) {
    return {
      ...defaultResult,
      disabled: true,
      tooltip: `You can only manage ${limit} cages a time`,
    };
  }

  const studyLinkData = selectedCages[0].study_link?.data;
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const cageInStudy = !!selectedCages[0].study_id;
  const cagesAssignedToSameStudy = selectedCages.every((cage) => cage.study_link?.data?.id === studyLinkData?.id);

  if (!cagesAssignedToSameStudy) {
    return {
      ...defaultResult,
      disabled: true,
      tooltip: 'All cages must be assigned to the same study',
    };
  }

  if (
    cageInStudy &&
    (_isNil(studyLinkData?.access) || !allowedAccessPermissions.includes(String(studyLinkData?.access ?? '')))
  ) {
    return {
      ...defaultResult,
      disabled: true,
      tooltip: 'You must have at least write access to the study to manage cages',
    };
  }

  return defaultResult;
};

export const generateColumns = (
  { metadata }: { metadata: Array<CageMetadataFilter> },
  columns: Record<string, boolean> | undefined,
  actions: Array<unknown>,
  addToStudy: boolean,
  dispatch: Dispatch<CagesAction>,
  storeDispatch: Dispatch<{ type: string; data?: unknown }>,
  handleRemoveCageFromStudy: (id: number | string, name: string) => Promise<void>
) => {
  let result = [
    {
      id: 'id',
      accessorKey: 'id',
      isVisible: false,
      lockVisibility: true,
    },
    {
      id: 'catalog',
      header: 'Cage No.',
      accessorKey: 'catalog',
      enableSorting: true,
      isVisible: columns?.catalog ?? false,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: Cage } }) => original.catalog || '',
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      enableSorting: true,
      isVisible: columns?.name ?? false,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: Cage } }) => original.name || '',
    },
    {
      id: 'population',
      header: 'Population',
      enableSorting: false,
      accessorFn: (row: Cage) => row.counts?.animals,
      isVisible: columns?.population ?? false,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row: { original } }: { row: { original: Cage } }) => original.counts?.animals || 0,
    },
  ];

  if (!addToStudy) {
    result = [
      ...result,
      {
        id: 'study',
        header: 'Study',
        accessorKey: 'study_id',
        enableSorting: true,
        isVisible: columns?.study ?? false,
        cell: ({ row: { original } }: { row: { original: Cage } }) => {
          const studyData = original.study_link?.data;
          const studyName = studyData?.name ?? '';
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          return studyData?.access && _isNil(studyData.deleted_at) ? (
            <Link className="link blue" to={webRoute('studies.show', { id: studyData.id })}>
              {studyName}
            </Link>
          ) : _isNil(studyData?.deleted_at) ? (
            studyName
          ) : (
            studyName + ' (Deleted)'
          );
        },
      } as any,
    ];
  }

  result = [
    ...result,
    {
      id: 'date',
      header: 'Created',
      accessorKey: 'created_at',
      enableSorting: true,
      isVisible: columns?.date ?? false,
      cell: ({ row: { original } }: { row: { original: Cage } }) => <DateTimeRenderer value={original.created_at} />,
    } as any,
    ...(fetchMetadataColumns({ metadata: metadata as any, columns }) as any),
  ];

  if (!addToStudy) {
    result = [
      ...result,
      {
        id: 'actions',
        header: '',
        lockVisibility: true,
        enableSorting: false,
        accessorKey: 'actions',
        isVisible: true,
        cell: ({ row }: { row: { original: Cage } }) => (
          <CageActionsCell
            cage={row.original as any}
            dispatch={dispatch as any}
            storeDispatch={storeDispatch as any}
            handleRemoveCageFromStudy={handleRemoveCageFromStudy}
          />
        ),
      } as any,
    ];
  }

  return result;
};

export const generateMetadataFilterOptions = (metadata: Array<CageMetadataFilter>) => {
  const result: Array<{
    value: ID;
    name: string;
    operations: Array<{ name: string; value: string }>;
    fetchFromApi?: {
      placeholder: string;
      url: string;
      params: { metadataGlossaryId: ID };
      fieldType: string;
      useModernApi: boolean;
    };
  }> = [];
  metadata?.forEach(({ field_type, id, name }) => {
    if (!metadataTypeIgnoredFilters.includes(field_type)) {
      if (field_type === 'multi_select') {
        result.push({
          value: id,
          name,
          operations: selectOperations,
          fetchFromApi: {
            placeholder: 'Multiselect options',
            url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
            params: { metadataGlossaryId: id },
            fieldType: 'multi_select',
            useModernApi: true,
          },
        });
      } else if (field_type === 'select') {
        result.push({
          value: id,
          name,
          operations: selectOperations,
          fetchFromApi: {
            placeholder: 'Select options',
            url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
            params: { metadataGlossaryId: id },
            fieldType: 'select',
            useModernApi: true,
          },
        });
      } else {
        result.push({
          value: id,
          name,
          operations: [
            {
              name: filterType.contains,
              value: field_type === 'date' ? filterType.eq_date : filterType.contains,
            },
            {
              name: filterType.not_contains,
              value: field_type === 'date' ? filterType.not_eq_date : filterType.not_contains,
            },
          ],
        });
      }
    }
  });
  return result;
};

export const generateFilterOptions = (filters: CageFiltersEntity, addToStudy: boolean) => {
  const result = [
    {
      value: 'animals.count',
      name: 'Population',
      operations: [
        {
          name: 'greater than',
          value: filterType.gt,
        },
        {
          name: 'less than',
          value: filterType.lt,
        },
        {
          name: 'empty',
          value: filterType.EMPTY,
        },
      ],
    },
  ];

  const metadataFilterOptions = generateMetadataFilterOptions(filters.metadata);
  if (_isNotEmpty(metadataFilterOptions)) {
    result.push(...(metadataFilterOptions as any));
  }

  if (_isNotEmpty(filters.fields)) {
    const filteredFields = addToStudy ? filters.fields.filter((field) => field.name !== 'Study') : filters.fields;

    filteredFields.forEach((field) => {
      const { name, value, options } = field;
      const fetchFromApi = filterOptionsFromApi(name);
      if (fetchFromApi) {
        result.push({
          value,
          name,
          operations: [
            {
              name: 'is equal to',
              value: filterType.eq,
              options: [] as any,
            } as any,
            {
              name: 'is not equal to',
              value: filterType.ne,
              options: [] as any,
            } as any,
          ],
          fetchFromApi: {
            placeholder: 'Search studies',
            url: 'studies.base',
          },
        } as any);
      } else {
        result.push({
          value,
          name,
          operations: [
            {
              name: 'is equal to',
              value: filterType.eq,
              options: options as any,
            } as any,
            {
              name: 'is not equal to',
              value: filterType.ne,
              options: options as any,
            } as any,
          ],
        });
      }
    });
  }

  return result;
};

/**
 * Reducer for Cages component state.
 * Note: After migration to TanStack Table v8, many states are now managed by the table itself.
 * This reducer only handles states that are not managed by TanStack Table.
 */
export interface CagesState {
  updating: boolean;
  apiErrors: boolean;
  data: Array<Cage>;
  columns: Array<unknown>;
  searchQuery: string;
  filterOptions: Array<unknown>;
  filters: Array<unknown>;
  filterMatch: boolean;
  selectedRows: Record<string, unknown>;
  total_pages: number;
  current_page: number;
  per_page: number;
  order: 'asc' | 'desc';
  sort: Array<string>;
  cageIdToPrint: number | string | null;
  actions: Array<unknown>;
  metadata: Array<unknown>;
}

export type CagesAction =
  | { type: 'SET_UPDATING'; data: boolean }
  | { type: 'SET_ERROR'; data: boolean }
  | { type: 'SET_COLUMNS'; data: Array<unknown> }
  | { type: 'SET_FILTER_OPTIONS'; data: Array<unknown> }
  | { type: 'SET_CAGE_ID_TO_PRINT'; data: number | string | null }
  | { type: 'SET_ACTIONS'; data: Array<unknown> }
  | { type: 'SET_METADATA'; data: Array<unknown> };

export const reducer = (state: CagesState, action: CagesAction) => {
  switch (action.type) {
    // Actions data from API
    case 'SET_ACTIONS':
      return {
        ...state,
        actions: action.data,
      };

    // Loading/updating states
    case 'SET_UPDATING':
      return {
        ...state,
        updating: action.data,
      };

    // Error states
    case 'SET_ERROR':
      return {
        ...state,
        apiErrors: action.data,
      };

    // Filter options from API
    case 'SET_FILTER_OPTIONS':
      return {
        ...state,
        filterOptions: action.data,
      };
    case 'SET_METADATA':
      return {
        ...state,
        metadata: action.data,
      };
    case 'SET_COLUMNS': {
      return {
        ...state,
        columns: action.data,
      };
    }

    // Cage ID for printing
    case 'SET_CAGE_ID_TO_PRINT':
      return {
        ...state,
        cageIdToPrint: action.data,
      };

    //
    // REMOVED ACTIONS (now managed by TanStack Table v8):
    // - SET_SEARCH_QUERY (handled by table search state)
    // - SET_SELECTED (handled by table rowSelection state)
    // - SET_PAGING (handled by table pagination state)
    // - SET_FILTERS (handled by table filtering)
    // - SET_SORT (handled by table sorting state)
    //

    default: {
      return { ...state };
    }
  }
};
